import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import type {
  CreateMcpServerInput,
  UpdateMcpServerInput,
} from '../types/mcp';

function useMcpServerApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).mcpServers, [spaceId]);
}

export function useMcpServers() {
  const spaceId = useSpaceId();
  const api = useMcpServerApi();
  return useQuery({
    queryKey: ['space', spaceId, 'mcp-servers'],
    queryFn: () => api.list(),
  });
}

export function useMcpServer(serverId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useMcpServerApi();
  return useQuery({
    queryKey: ['space', spaceId, 'mcp-servers', serverId],
    queryFn: () => api.get(serverId!),
    enabled: !!serverId,
  });
}

export function useCreateMcpServer() {
  const spaceId = useSpaceId();
  const api = useMcpServerApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMcpServerInput) => api.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'mcp-servers'] });
    },
  });
}

export function useUpdateMcpServer() {
  const spaceId = useSpaceId();
  const api = useMcpServerApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      serverId,
      input,
    }: {
      serverId: string;
      input: UpdateMcpServerInput;
    }) => api.update(serverId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'mcp-servers'] });
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'mcp-servers', variables.serverId],
      });
    },
  });
}

export function useDeleteMcpServer() {
  const spaceId = useSpaceId();
  const api = useMcpServerApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (serverId: string) => api.delete(serverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'mcp-servers'] });
    },
  });
}

export function useSyncMcpServer() {
  const spaceId = useSpaceId();
  const api = useMcpServerApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { serverId: string; returnUrl?: string }) =>
      api.sync(input.serverId, input.returnUrl),
    onSuccess: (_data, { serverId }) => {
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'mcp-servers', serverId],
      });
      // The list shows last-sync timestamps + tool counts — refresh it too.
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'mcp-servers'] });
    },
  });
}
